import { CreateEstimateFormData } from '@/types/estimates/CreateEstimateFormData';

type FlexibilitySource = {
  allowAgencyModification?: boolean;
  showQuoteName?: boolean;
  showSummaryAndImportantPoints?: boolean;
  showPriceIncludesExcludes?: boolean;
  showModificationCancellationConditions?: boolean;
  showDates?: boolean;
  allowAgencyInternationalFlights?: boolean;
  allowAgencyMarginRate?: boolean;
  allowAgencyAdditionalMargin?: boolean;
};

const toFlexRadio = (value: boolean): 'true' | 'false' =>
  value ? 'true' : 'false';

/** Maps estimate flexibility booleans to Yes/No radio string values. */
export const estimateFlexibilityToFormValues = (
  estimate: FlexibilitySource,
): Pick<
  CreateEstimateFormData,
  | 'allowAgencyModification'
  | 'showQuoteName'
  | 'showSummaryAndImportantPoints'
  | 'showPriceIncludesExcludes'
  | 'showModificationCancellationConditions'
  | 'showDates'
  | 'allowAgencyInternationalFlights'
  | 'allowAgencyMarginRate'
  | 'allowAgencyAdditionalMargin'
> => ({
  allowAgencyModification: toFlexRadio(
    estimate.allowAgencyModification ?? false,
  ),
  showQuoteName: toFlexRadio(estimate.showQuoteName ?? true),
  showSummaryAndImportantPoints: toFlexRadio(
    estimate.showSummaryAndImportantPoints ?? true,
  ),
  showPriceIncludesExcludes: toFlexRadio(
    estimate.showPriceIncludesExcludes ?? true,
  ),
  showModificationCancellationConditions: toFlexRadio(
    estimate.showModificationCancellationConditions ?? true,
  ),
  showDates: toFlexRadio(estimate.showDates ?? true),
  allowAgencyInternationalFlights: toFlexRadio(
    estimate.allowAgencyInternationalFlights ?? false,
  ),
  allowAgencyMarginRate: toFlexRadio(estimate.allowAgencyMarginRate ?? false),
  allowAgencyAdditionalMargin: toFlexRadio(
    estimate.allowAgencyAdditionalMargin ?? false,
  ),
});

/** Maps dirty flexibility radio fields back to booleans for the API. */
type FlexibilityField = keyof ReturnType<
  typeof estimateFlexibilityToFormValues
>;
export type FlexibilityDirtyFields = Partial<Record<FlexibilityField, boolean>>;

export const flexibilityFormValuesToApi = (
  data: CreateEstimateFormData,
  dirtyFields: FlexibilityDirtyFields,
) => ({
  ...(dirtyFields.allowAgencyModification && {
    allowAgencyModification: data.allowAgencyModification === 'true',
  }),
  ...(dirtyFields.showQuoteName && {
    showQuoteName: data.showQuoteName === 'true',
  }),
  ...(dirtyFields.showSummaryAndImportantPoints && {
    showSummaryAndImportantPoints:
      data.showSummaryAndImportantPoints === 'true',
  }),
  ...(dirtyFields.showPriceIncludesExcludes && {
    showPriceIncludesExcludes: data.showPriceIncludesExcludes === 'true',
  }),
  ...(dirtyFields.showModificationCancellationConditions && {
    showModificationCancellationConditions:
      data.showModificationCancellationConditions === 'true',
  }),
  ...(dirtyFields.showDates && {
    showDates: data.showDates === 'true',
  }),
  ...(dirtyFields.allowAgencyInternationalFlights && {
    allowAgencyInternationalFlights:
      data.allowAgencyInternationalFlights === 'true',
  }),
  ...(dirtyFields.allowAgencyMarginRate && {
    allowAgencyMarginRate: data.allowAgencyMarginRate === 'true',
  }),
  ...(dirtyFields.allowAgencyAdditionalMargin && {
    allowAgencyAdditionalMargin: data.allowAgencyAdditionalMargin === 'true',
  }),
});
